/**
 * Schemas e contratos de `/agendamentos` (`03-endpoints-draft.md §7`, FL-09).
 *
 * O agendamento é o que sustenta a fila de retorno: criar um define
 * `leads.proximo_retorno_em`, e é esse campo que produz o realce de atraso da
 * TELA-02 (RN-25A). Por isso nenhuma rota daqui é "só calendário" — toda escrita
 * mexe no lead e gera interação (RN-22).
 */
import { z } from 'zod'

import { zDataFiltro, zIdCatalogo, zInstante, zTextoAtendimento, zUuid } from '../comum/campos'
import { schemaListaDeEnum, schemaPaginacaoCursor } from '../comum/paginacao'
import type { ReferenciaCatalogo, ReferenciaPessoa } from '../leads/contratos'
import { STATUS_AGENDAMENTO, type StatusAgendamento, type StatusLead } from '../leads/enums'

/**
 * Janela máxima do calendário, em dias (§7): a grade da TELA-08 não é paginada, e
 * um `de`/`ate` de um ano devolveria a agenda inteira da equipe em um payload.
 * 31 cobre a visão de mês, que é a maior da tela.
 */
export const CALENDARIO_JANELA_MAXIMA_DIAS = 31

/**
 * ⚠️ **Q17 em aberto**: "duração padrão de um agendamento e checagem de conflito
 * de horário?". Enquanto não houver resposta, `terminaEm` fica `null` quando o
 * cliente não informa — o banco aceita (`termina_em` é nulável) e a grade trata
 * como compromisso pontual. Não se inventa 30 nem 60 minutos: um valor errado
 * gravado em milhares de linhas é mais caro de desfazer do que um campo nulo.
 */
export const schemaCriarAgendamento = z
  .object({
    leadUuid: zUuid,
    motivoId: zIdCatalogo,
    iniciaEm: zInstante,
    terminaEm: zInstante.nullish(),
    diaInteiro: z.boolean().default(false),
    titulo: z.string().trim().max(160).nullish(),
    observacao: z.string().trim().max(1000).nullish(),
    /** Ausente = o próprio autor. Preenchido exige `AGENDAMENTO_CRIAR_PARA_OUTRO`. */
    responsavelUuid: zUuid.optional(),
  })
  .strict()

export type EntradaCriarAgendamento = z.infer<typeof schemaCriarAgendamento>

export const schemaAtualizarAgendamento = z
  .object({
    titulo: z.string().trim().max(160).nullish(),
    observacao: z.string().trim().max(1000).nullish(),
    terminaEm: zInstante.nullish(),
    diaInteiro: z.boolean().optional(),
    motivoId: zIdCatalogo.optional(),
  })
  .strict()
  .refine((valor) => Object.keys(valor).length > 0, 'Informe ao menos um campo para alterar.')

export type EntradaAtualizarAgendamento = z.infer<typeof schemaAtualizarAgendamento>

/**
 * Concluir, cancelar e "não compareceu" registram interação no lead — logo, texto
 * de atendimento com o mesmo mínimo do RN-11. `concluir` é o único em que a
 * mensagem é opcional: o desfecho do compromisso já é informação, e o atendente
 * costuma registrar o conteúdo da conversa em uma interação separada.
 */
export const schemaConcluirAgendamento = z
  .object({ mensagem: zTextoAtendimento.optional() })
  .strict()

export const schemaCancelarAgendamento = z.object({ motivo: zTextoAtendimento }).strict()

export const schemaReagendar = z
  .object({
    iniciaEm: zInstante,
    terminaEm: zInstante.nullish(),
    motivoId: zIdCatalogo.optional(),
    observacao: z.string().trim().max(1000).nullish(),
    motivo: zTextoAtendimento,
  })
  .strict()

export type EntradaReagendar = z.infer<typeof schemaReagendar>

export const CAMPOS_ORDENACAO_AGENDAMENTO = ['iniciaEm', 'criadoEm', 'status'] as const

export type CampoOrdenacaoAgendamento = (typeof CAMPOS_ORDENACAO_AGENDAMENTO)[number]

export const schemaFiltrosAgendamento = schemaPaginacaoCursor.extend({
  de: zDataFiltro.optional(),
  ate: zDataFiltro.optional(),
  status: schemaListaDeEnum(STATUS_AGENDAMENTO, 'Status de agendamento'),
  equipeId: zIdCatalogo.optional(),
  motivoId: zIdCatalogo.optional(),
  responsavelUuid: zUuid.optional(),
  leadUuid: zUuid.optional(),
  /** Compromissos vencidos e não tratados (RN-25A). */
  vencidos: z.enum(['true']).optional(),
})

export type FiltrosAgendamento = z.infer<typeof schemaFiltrosAgendamento>

export const schemaCalendario = z.object({
  de: zDataFiltro,
  ate: zDataFiltro,
  equipeId: zIdCatalogo.optional(),
  motivoId: zIdCatalogo.optional(),
  responsavelUuid: zUuid.optional(),
})

export type FiltrosCalendario = z.infer<typeof schemaCalendario>

// ---------------------------------------------------------------------------
// Contratos de resposta
// ---------------------------------------------------------------------------

export interface AgendamentoNaLista {
  readonly uuid: string
  readonly status: StatusAgendamento
  readonly iniciaEm: string
  readonly terminaEm: string | null
  readonly diaInteiro: boolean
  readonly titulo: string | null
  readonly observacao: string | null
  readonly concluidoEm: string | null
  /** Cor do motivo — é ela que pinta o evento na grade da TELA-08. */
  readonly motivo: ReferenciaCatalogo & { readonly cor: string }
  readonly responsavel: ReferenciaPessoa
  readonly equipe: ReferenciaCatalogo | null
  readonly lead: {
    readonly uuid: string
    readonly status: StatusLead
    readonly clienteNome: string
    readonly clienteContato: string
  }
  /** Vencido e ainda `AGENDADO` (RN-25A). */
  readonly vencido: boolean
  readonly reagendadoDeUuid: string | null
}

/** Grade da TELA-08: já agrupada por dia civil, em um payload só (§7). */
export interface DiaDoCalendario {
  /** `YYYY-MM-DD` no fuso operacional. */
  readonly dia: string
  readonly total: number
  readonly agendamentos: ReadonlyArray<AgendamentoNaLista>
}

export interface RespostaCalendario {
  readonly de: string
  readonly ate: string
  readonly dias: ReadonlyArray<DiaDoCalendario>
  readonly total: number
}
